import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Archive, Calendar, User, Eye, ArchiveRestore, Trash2 } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArchivesPage = () => {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchArchives();
  }, []);

  const fetchArchives = async () => {
    try {
      const response = await axios.get(`${API}/cases`);
      // Filter only archived cases
      const archived = response.data.filter(c => c.archive_status === 'archived');
      setArchives(archived);
    } catch (error) {
      console.error("Error fetching archives:", error);
      toast.error("Failed to load archives");
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async () => {
    if (!selectedCase) return;
    
    try {
      await axios.post(`${API}/cases/${selectedCase.id}/unarchive`);
      toast.success("Case unarchived successfully");
      fetchArchives();
    } catch (error) {
      console.error("Error unarchiving case:", error);
      toast.error("Failed to unarchive case");
    } finally {
      setUnarchiveDialogOpen(false);
      setSelectedCase(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedCase) return;
    
    try {
      await axios.delete(`${API}/cases/${selectedCase.id}`);
      toast.success("Case deleted permanently");
      fetchArchives();
    } catch (error) {
      console.error("Error deleting case:", error);
      toast.error("Failed to delete case");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedCase(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#FFFFFF' }}>
            Archives
          </h1>
          <p className="text-lg" style={{ color: '#F5F7FA' }}>
            Archived cases and summaries
          </p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#245451' }}></div>
            <p className="mt-4" style={{ color: '#F5F7FA' }}>Loading archives...</p>
          </div>
        ) : archives.length === 0 ? (
          <Card className="glass-card border-dashed border-2">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Archive className="h-16 w-16 mb-4" style={{ color: '#84a2ac' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#1A1A1A' }}>No archived cases</h3>
              <p style={{ color: '#406962' }}>Archived cases will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {archives.map((archive) => (
              <Card 
                key={archive.id}
                className="glass-card hover:shadow-lg transition-all"
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-2" style={{ color: '#245451' }}>
                        {archive.case_name}
                      </CardTitle>
                      <CardDescription className="space-y-1" style={{ color: '#64748B' }}>
                        <div className="flex items-center text-sm">
                          <User className="h-3.5 w-3.5 mr-1.5" />
                          {archive.patient_name}
                        </div>
                        <div className="flex items-center text-sm">
                          <Calendar className="h-3.5 w-3.5 mr-1.5" />
                          {new Date(archive.date_of_injury).toLocaleDateString()}
                        </div>
                      </CardDescription>
                    </div>
                    <span 
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: '#F1F5F9', color: '#64748B' }}
                    >
                      Archived
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button
                      onClick={() => navigate(`/case/${archive.id}`)}
                      className="w-full"
                      style={{ backgroundColor: '#245451' }}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      View Case
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          setSelectedCase(archive);
                          setUnarchiveDialogOpen(true);
                        }}
                        variant="outline"
                        className="flex-1"
                        style={{ borderColor: '#E2E8F0', color: '#245451' }}
                      >
                        <ArchiveRestore className="mr-1 h-3 w-3" />
                        Unarchive
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedCase(archive);
                          setDeleteDialogOpen(true);
                        }}
                        variant="outline"
                        className="flex-1"
                        style={{ borderColor: '#E2E8F0', color: '#DC2626' }}
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs mt-3" style={{ color: '#84a2ac' }}>
                    Created {new Date(archive.created_at).toLocaleDateString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Unarchive Confirmation Dialog */}
      <AlertDialog open={unarchiveDialogOpen} onOpenChange={setUnarchiveDialogOpen}>
        <AlertDialogContent className="glass-card">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: '#245451' }}>Unarchive Case?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#64748B' }}>
              This will move the case back to your active cases list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: '#E2E8F0', color: '#64748B' }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleUnarchive}
              style={{ backgroundColor: '#245451' }}
            >
              Unarchive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="glass-card">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: '#DC2626' }}>Delete Case Permanently?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#64748B' }}>
              This action cannot be undone. This will permanently delete the case and all associated files.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: '#E2E8F0', color: '#64748B' }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              style={{ backgroundColor: '#DC2626' }}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default ArchivesPage;